import type { ProjectTask } from '../types';

function dayStart(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/** Earliest child start → latest child end (inclusive calendar days). */
export function rollupDatesFromChildren(
  children: ProjectTask[],
): { startDate: Date; endDate: Date } | null {
  if (children.length === 0) return null;
  let minStart: Date | null = null;
  let maxEnd: Date | null = null;
  for (const c of children) {
    const s = dayStart(new Date(c.startDate));
    const e = dayStart(new Date(c.endDate));
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) continue;
    if (!minStart || s < minStart) minStart = s;
    if (!maxEnd || e > maxEnd) maxEnd = e;
  }
  if (!minStart || !maxEnd) return null;
  return { startDate: minStart, endDate: maxEnd };
}

export function sameRollupDates(
  parent: Pick<ProjectTask, 'startDate' | 'endDate'>,
  rollup: { startDate: Date; endDate: Date },
): boolean {
  return (
    dayStart(new Date(parent.startDate)).getTime() === rollup.startDate.getTime()
    && dayStart(new Date(parent.endDate)).getTime() === rollup.endDate.getTime()
  );
}

export function parentRollupPatch(
  parentId: string,
  tasks: ProjectTask[],
  excludeTaskId?: string,
): { parentId: string; startDate: Date; endDate: Date } | null {
  const parent = tasks.find((t) => t.id === parentId);
  if (!parent) return null;
  const children = tasks.filter(
    (t) => t.parentTaskId === parentId && t.id !== excludeTaskId,
  );
  const rollup = rollupDatesFromChildren(children);
  if (!rollup || sameRollupDates(parent, rollup)) return null;
  return { parentId, ...rollup };
}

export type TaskDateOverride = {
  taskId: string;
  startDate: Date;
  endDate: Date;
};

/** Earliest start → latest finish across a group’s parent tasks and their children. */
export function rollupGroupTaskDates(
  groupTasks: ProjectTask[],
  childTasks: ProjectTask[],
  override?: TaskDateOverride | null,
): { startDate: Date; endDate: Date } | null {
  const apply = (t: ProjectTask): ProjectTask =>
    override && t.id === override.taskId
      ? { ...t, startDate: override.startDate, endDate: override.endDate }
      : t;
  return rollupDatesFromChildren([...groupTasks, ...childTasks].map(apply));
}

/** After a child changes, return parent date patch if needed (optional child date override). */
export function parentRollupPatchAfterChildChange(
  childTaskId: string,
  tasks: ProjectTask[],
  childDateOverride?: { startDate: Date; endDate: Date },
): { parentId: string; startDate: Date; endDate: Date } | null {
  const child = tasks.find((t) => t.id === childTaskId);
  if (!child?.parentTaskId) return null;
  const patchedTasks = childDateOverride
    ? tasks.map((t) => (t.id === childTaskId ? { ...t, ...childDateOverride } : t))
    : tasks;
  return parentRollupPatch(child.parentTaskId, patchedTasks);
}
